import { useState, useRef, useEffect } from 'react';
import { Upload, PenTool, X } from 'lucide-react';
import { useI18n } from '@/contexts/i18n-hook';

interface AssinaturaManagerProps {
  assinaturaUrl?: string;
  assinaturaDigital?: string;
  onChange?: (assinaturaUrl?: string, assinaturaDigital?: string) => void;
  modoEdicao?: boolean;
  label?: string;
}

export function AssinaturaManager({
  assinaturaUrl,
  assinaturaDigital,
  onChange,
  modoEdicao = false,
  label,
}: AssinaturaManagerProps) {
  const { t } = useI18n();
  // Sem rótulo vindo de fora o componente usa o seu — traduzido, não fixo em pt.
  const rotulo = label ?? t('apreciacaoTabelas.assinatura.rotuloPadrao');
  const [modo, setModo] = useState<'upload' | 'digital' | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // URLs que são só nome de arquivo estático (Lauan.jpg, Luciano.jpg) devem ser servidas de /assets/
  const urlParaExibicao = (url: string | undefined): string | undefined => {
    if (!url) return undefined;
    if (url.startsWith('data:') || url.startsWith('http') || url.startsWith('/')) return url;
    if (url === 'Lauan.jpg' || url === 'Luciano.jpg') return `/assets/${url}`;
    return url;
  };

  useEffect(() => {
    const paraExibir = urlParaExibicao(assinaturaUrl);
    if (paraExibir) {
      setPreviewUrl(paraExibir);
    } else if (assinaturaDigital) {
      setPreviewUrl(assinaturaDigital);
    } else {
      setPreviewUrl(null);
    }
  }, [assinaturaUrl, assinaturaDigital]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setPreviewUrl(result);
        onChange?.(result, undefined);
      };
      reader.readAsDataURL(file);
    }
  };

  const iniciarDesenho = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
  };

  const desenhar = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const pararDesenho = () => {
    setIsDrawing(false);
    if (canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL();
      setPreviewUrl(dataUrl);
      onChange?.(undefined, dataUrl);
    }
  };

  const limparCanvas = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        setPreviewUrl(null);
        onChange?.(undefined, undefined);
      }
    }
  };

  const limparAssinatura = () => {
    setPreviewUrl(null);
    onChange?.(undefined, undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (modo === 'digital' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }
  }, [modo]);

  // Padrão de tamanho limite das assinaturas cadastradas (mesmo para inseridas/externas)
  const classeTamanhoAssinatura = 'max-h-32 max-w-[12rem] object-contain';

  if (!modoEdicao) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
          {rotulo}
        </label>
        {previewUrl ? (
          <div className="border border-gray-300 dark:border-schmersal-gray-dark rounded-md p-2 inline-block">
            <img key={previewUrl} src={previewUrl} alt={t('apreciacaoTabelas.assinatura.alt')} className={classeTamanhoAssinatura} />
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('apreciacaoTabelas.assinatura.vazio')}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700 dark:text-white">
        {rotulo}
      </label>

      {!modo && !previewUrl && (
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setModo('upload')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-schmersal-gray-dark rounded-md 
                     hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark text-gray-700 dark:text-white"
          >
            <Upload className="h-4 w-4" />
            {t('apreciacaoTabelas.assinatura.acao.upload')}
          </button>
          <button
            type="button"
            onClick={() => setModo('digital')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-schmersal-gray-dark rounded-md 
                     hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark text-gray-700 dark:text-white"
          >
            <PenTool className="h-4 w-4" />
            {t('apreciacaoTabelas.assinatura.acao.digital')}
          </button>
        </div>
      )}

      {modo === 'upload' && (
        <div className="space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="block w-full text-sm text-gray-500 dark:text-gray-400
                     file:mr-4 file:py-2 file:px-4
                     file:rounded-md file:border-0
                     file:text-sm file:font-semibold
                     file:bg-blue-50 file:text-blue-700
                     hover:file:bg-blue-100
                     dark:file:bg-schmersal-gray-dark dark:file:text-schmersal-blue"
          />
          <button
            type="button"
            onClick={() => setModo(null)}
            className="text-sm text-gray-600 dark:text-gray-400 hover:underline"
          >
            {t('comum.acao.cancelar')}
          </button>
        </div>
      )}

      {modo === 'digital' && (
        <div className="space-y-2">
          <div className="border border-gray-300 dark:border-schmersal-gray-dark rounded-md p-2 bg-white">
            <canvas
              ref={canvasRef}
              width={400}
              height={150}
              onMouseDown={iniciarDesenho}
              onMouseMove={desenhar}
              onMouseUp={pararDesenho}
              onMouseLeave={pararDesenho}
              className="border border-gray-200 dark:border-schmersal-gray-dark rounded cursor-crosshair"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={limparCanvas}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-schmersal-gray-dark rounded-md 
                       hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark text-gray-700 dark:text-white"
            >
              {t('comum.acao.limpar')}
            </button>
            <button
              type="button"
              onClick={() => setModo(null)}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-schmersal-gray-dark rounded-md 
                       hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark text-gray-700 dark:text-white"
            >
              {t('apreciacaoTabelas.assinatura.acao.concluir')}
            </button>
          </div>
        </div>
      )}

      {previewUrl && modo === null && (
        <div className="relative">
          <div className="border border-gray-300 dark:border-schmersal-gray-dark rounded-md p-2 inline-block">
            <img key={previewUrl} src={previewUrl} alt={t('apreciacaoTabelas.assinatura.alt')} className={classeTamanhoAssinatura} />
          </div>
          <button
            type="button"
            onClick={limparAssinatura}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
            title={t('apreciacaoTabelas.assinatura.acao.remover')}
          >
            <X className="h-4 w-4" />
          </button>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={() => {
                setModo('upload');
                limparAssinatura();
              }}
              className="text-sm text-blue-600 dark:text-schmersal-blue hover:underline"
            >
              {t('apreciacaoTabelas.assinatura.acao.alterarUpload')}
            </button>
            <button
              type="button"
              onClick={() => {
                setModo('digital');
                limparAssinatura();
              }}
              className="text-sm text-blue-600 dark:text-schmersal-blue hover:underline"
            >
              {t('apreciacaoTabelas.assinatura.acao.alterarDigital')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

